/**
 * This collection is the attribute datum for a single asset
 * @class
 */
import Base from '@netx/core/lib/netx-collection';
import Model from '../models/attributeData';

export default class AttributeDatum extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @override */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;
		super(models, opts);

		/**
		 * @memberof module:asset/models/attributeDatum
		 * @alias assetAttributes
		 * @type {module:asset/collections/attributes}
		 */
		this.assetAttributes = opts.assetAttributes;

		// Hey, listen!
		this.listenTo(this, 'sync', this.clearChanges).listenTo(
			this,
			'metadataLookup:complete',
			this.metadataLookupComplete,
		);
	}

	/**
	 * Clear value changes
	 * @method
	 */
	clearChanges() {
		this.invoke('clearChanges');
	}

	/**
	 * Get a single attribute data model
	 * @method
	 * @param {number} tempateId Attribute template id (will support name as well until totally phased out)
	 * @param {string} name Template name
	 * @return {Netx.Models.AttributeData}
	 */
	getAttribute(templateId, name) {
		var attrModel;

		// Find asset attribute template model
		attrModel =
			name != null
				? (this.assetAttributes || Netx.attributes).findWhere({name: name})
				: (this.assetAttributes || Netx.attributes).get(templateId);

		// Get existing asset attribute data record
		return attrModel && this.get(attrModel.id);
	}

	/**
	 * Get all attribute value changes
	 * @method
	 * @return {array}
	 */
	getChanges() {
		return _.compact(
			this.map((attr) => {
				if (attr.hasValueChanged()) {
					return _.pick.apply(
						_,
						[attr.self()].concat(_.keys(Model.GetDefaults())),
					);
				}
			}),
		);
	}

	/**
	 * Get a single attribute value
	 * @method
	 * @param {number|string} templateId Attribute template id (or name)
	 * @return {mixed}
	 */
	getValue(templateId) {
		var attributeDataModel = this.getAttribute(templateId);
		if (attributeDataModel) {
			return attributeDataModel.getValue();
		}
	}

	/**
	 * Get all attribute values
	 * @method
	 * @return {array}
	 */
	getValues() {
		return this.map((attrData) => attrData.getValue());
	}

	/**
	 * Get all read-only attributes
	 * @method
	 * @return {array}
	 */
	getReadOnly() {
		const readOnly = this.filter((attrData) => !attrData.allows('edit'));
		return readOnly.length ? readOnly : null;
	}

	/**
	 * Get all writable attributes
	 * @method
	 * @return {array}
	 */
	getWritable() {
		const writable = this.filter((attrData) => attrData.allows('edit'));
		return writable.length ? writable : null;
	}

	/**
	 * @method
	 * @param {module:asset/models/attributeData} attrData Attribute data
	 * @param {array} matches Matches
	 */
	metadataLookupComplete(attrData, matches) {
		let attr;
		_.each(matches, (match) => {
			attr = this.getAttribute(null, match.label);
			attr &&
				attr.setValue(match.value, {
					autofilled: true,
				});
		});
	}

	/**
	 * Reset all values
	 * @method
	 * @param {object} opts Options
	 * @see Netx.Collections.AttributeDatum
	 */
	resetValues(opts) {
		return this.invoke('resetValues', opts);
	}

	/**
	 * Set value on a single attribute
	 * @method
	 * @param {number} templateId Attribute template id
	 * @param {mixed} val Value
	 * @param {object} opts Options
	 */
	setValue(templateId, val, opts) {
		var attributeDataModel = this.getAttribute(templateId);
		attributeDataModel && attributeDataModel.setValue(val, opts);
	}

	/** @type {string} */
	static get __name__() {
		return 'AttributeDatum';
	}
}
